import { AlertTriangle, PackagePlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type {
  CartLineCustomization,
  DraftOrder,
  DraftOrderLine,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { withoutLine } from '../api/mappers';
import { useUpdateDraftLine, useUpdateDraftOrder } from '../hooks/use-draft-order-mutations';
import { firstFieldMessage, isStateConflict } from '../schemas/draft-order-rules';

import { AddProductLineDialog } from './add-product-line-dialog';
import { CustomLineDialog } from './custom-line-dialog';
import { EditLineDialog } from './edit-line-dialog';

function CustomizationChip({ customization }: { customization: CartLineCustomization }) {
  const { t } = useTranslation('draftOrders');
  const format = useFormat();
  const value = customization.valueLabel ?? String(customization.value);
  const delta = customization.priceDelta.amount;
  return (
    <Badge variant="secondary" className="font-normal">
      {delta === 0
        ? `${customization.label}: ${value}`
        : t('lines.delta', {
            label: customization.label,
            value,
            delta: `${delta > 0 ? '+' : ''}${format.money(customization.priceDelta)}`,
          })}
    </Badge>
  );
}

/** Commits on blur or Enter; an invalid entry snaps back to the saved quantity. */
function QuantityInput({
  line,
  disabled,
  onCommit,
}: {
  line: DraftOrderLine;
  disabled: boolean;
  onCommit: (quantity: number) => void;
}) {
  const { t } = useTranslation('draftOrders');
  const [text, setText] = useState(String(line.quantity));
  const commit = () => {
    const value = Number(text);
    if (!/^\d+$/.test(text.trim()) || value < 1 || value > 99) {
      setText(String(line.quantity));
      return;
    }
    if (value !== line.quantity) onCommit(value);
  };
  return (
    <Input
      type="number"
      inputMode="numeric"
      min={1}
      max={99}
      value={text}
      disabled={disabled}
      aria-label={t('lines.quantityOf', { title: line.title })}
      className="ml-auto h-8 w-16 text-right tabular-nums"
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
}

export interface DraftLinesCardProps {
  storeId: string;
  draft: DraftOrder;
  editable: boolean;
}

/**
 * The lines as the backend priced them. Adding or removing resends the whole list (the backend
 * replaces it); a quantity or an edit goes to the line alone. The card re-renders from the
 * re-priced response.
 */
export function DraftLinesCard({ storeId, draft, editable }: DraftLinesCardProps) {
  const { t } = useTranslation('draftOrders');
  const format = useFormat();
  const message = useErrorMessage();
  const update = useUpdateDraftOrder(storeId, 'draftOrders:lines.saved');
  const lineUpdate = useUpdateDraftLine(storeId, 'draftOrders:lines.saved');
  const [dialog, setDialog] = useState<'product' | 'custom' | null>(null);
  const [editing, setEditing] = useState<DraftOrderLine | null>(null);

  const busy = update.isPending || lineUpdate.isPending;
  const remove = (lineId: string) => {
    lineUpdate.reset();
    update.mutate({ id: draft.id, body: { lines: withoutLine(draft.lines, lineId) } });
  };
  const setQuantity = (lineId: string, quantity: number) => {
    update.reset();
    lineUpdate.mutate({ id: draft.id, lineId, body: { quantity } });
  };
  const error = update.error ?? lineUpdate.error;

  return (
    <Card className="gap-0 py-0" data-tour="draft-orders.detail.lines">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b py-4">
        <CardTitle>{t('lines.title')}</CardTitle>
        {editable ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setDialog('custom')}>
              <Plus aria-hidden />
              {t('lines.addCustom')}
            </Button>
            <Button size="sm" onClick={() => setDialog('product')}>
              <PackagePlus aria-hidden />
              {t('lines.addProduct')}
            </Button>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="px-0">
        {error ? (
          <Alert variant="destructive" role="alert" className="mx-6 my-3 w-auto">
            <AlertDescription>
              {isStateConflict(error)
                ? t('errors.locked')
                : (firstFieldMessage(error) ?? message(error))}
            </AlertDescription>
          </Alert>
        ) : null}
        {draft.lines.length === 0 ? (
          <p className="text-muted-foreground px-6 py-6 text-sm">{t('lines.empty')}</p>
        ) : (
          <Table aria-label={t('lines.title')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">{t('lines.item')}</TableHead>
                <TableHead className="text-right">{t('lines.quantity')}</TableHead>
                <TableHead className="text-right">{t('lines.unitPrice')}</TableHead>
                <TableHead className="text-right">{t('lines.total')}</TableHead>
                {editable ? <TableHead className="w-12 pr-6" /> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {draft.lines.map((line) => (
                <TableRow key={line.id} data-unavailable={line.available ? undefined : 'true'}>
                  <TableCell className="pl-6 align-top whitespace-normal">
                    <div className="min-w-0 space-y-1">
                      <p className="font-medium">{line.title}</p>
                      {line.variantTitle ? (
                        <p className="text-muted-foreground text-xs">{line.variantTitle}</p>
                      ) : null}
                      <p className="text-muted-foreground flex flex-wrap gap-x-2 text-xs">
                        {line.sku ? <code>{line.sku}</code> : null}
                        {line.kind === 'custom' ? <span>{t('lines.custom')}</span> : null}
                        {!line.requiresShipping ? <span>{t('lines.noShipping')}</span> : null}
                        {line.priceOverridden ? <span>{t('lines.overridden')}</span> : null}
                      </p>
                      {line.customizations.length > 0 ? (
                        <ul
                          className="flex flex-wrap gap-1 pt-1"
                          aria-label={t('lines.customizations')}
                        >
                          {line.customizations.map((c) => (
                            <li key={c.key}>
                              <CustomizationChip customization={c} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {!line.available ? (
                        <p className="text-destructive flex items-center gap-1 text-xs font-medium">
                          <AlertTriangle className="size-3.5" aria-hidden />
                          {t(`lines.unavailable.${line.unavailableReason ?? 'unavailable'}`)}
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-right align-top tabular-nums">
                    {editable ? (
                      <QuantityInput
                        key={line.quantity}
                        line={line}
                        disabled={busy}
                        onCommit={(quantity) => setQuantity(line.id, quantity)}
                      />
                    ) : (
                      line.quantity
                    )}
                  </TableCell>
                  <TableCell className="text-right align-top tabular-nums">
                    {format.money(line.unitPrice)}
                  </TableCell>
                  <TableCell className="text-right align-top font-medium tabular-nums">
                    {format.money(line.total)}
                  </TableCell>
                  {editable ? (
                    <TableCell className="pr-6 text-right align-top whitespace-nowrap">
                      {line.kind === 'custom' || line.productId ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          disabled={busy}
                          aria-label={t('lines.edit', { title: line.title })}
                          onClick={() => setEditing(line)}
                        >
                          <Pencil aria-hidden />
                        </Button>
                      ) : null}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={busy}
                        aria-label={t('lines.remove', { title: line.title })}
                        onClick={() => remove(line.id)}
                      >
                        <Trash2 aria-hidden />
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      {dialog === 'product' ? (
        <AddProductLineDialog
          storeId={storeId}
          draft={draft}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'custom' ? (
        <CustomLineDialog
          storeId={storeId}
          draft={draft}
          open
          onOpenChange={(open) => {
            if (!open) setDialog(null);
          }}
        />
      ) : null}
      {editing?.kind === 'custom' ? (
        <CustomLineDialog
          storeId={storeId}
          draft={draft}
          line={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
      {editing?.kind === 'variant' ? (
        <EditLineDialog
          storeId={storeId}
          draft={draft}
          line={editing}
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
        />
      ) : null}
    </Card>
  );
}
